import type { FC, ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";

import { ReferralPersonalCodeShareDialog } from "@ui/components/ReferralPersonalCode/components/ReferralPersonalCodeShareDialog/ReferralPersonalCodeShareDialog";
import { replaceStringPlaceholder } from "@ui/utils/replaceStringPlaceholder/replaceStringPlaceholder";
import { type ShareConfig, tryNativeShare } from "@ui/utils/share/share";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { ReferralPersonalCodeBlocks } from "../../ReferralPersonalCodeBlocks";
import type { ReferralPersonalCodeIconPropsType } from "../../ReferralPersonalCodeIcon";
import { ReferralPersonalCodeSmallText } from "../../ReferralPersonalCodeSmallText";
import { ReferralPersonalCodeWithBackground } from "../ReferralPersonalCodeWithBackground/ReferralPersonalCodeWithBackground";

import styles from "../../ReferralPersonalCode.module.scss";

const { sanitize } = dompurify;

export type ReferralPersonalCodeVariantButtonPropsType = {
  topHeader?: string;
  code: string;
  header?: string;
  codeTitle?: string;
  shareTitle?: string;
  description?: string;
  variant?: "default" | "dashboard" | "withBackgroundImage";
  referralPageVariant?: boolean;
  items: ReferralPersonalCodeIconPropsType[];
  infoTitle?: string;
  smallText?: string;
  backgroundImage?: string;
  backgroundImageTablet?: string;
  backgroundImageMobile?: string;
  showCta?: boolean;
  ctaRow?: ReactElement;
  className?: string;
  withBottomCta?: boolean;
  bottomCtaLabel?: string;
  bottomCtaLink?: string;
  isHidden?: boolean;
  isLegacyStyled?: boolean;
  shareCodeVariant?: string;
  shareConfig?: ShareConfig;
};

const replaceUrlCode = (url: string, code: string) => {
  return replaceStringPlaceholder(
    url,
    [
      {
        placeholder: "referralCode",
        value: code,
      },
    ],
    false
  );
};

export const ReferralPersonalCodeVariantButton: FC<ReferralPersonalCodeVariantButtonPropsType> = ({
  topHeader,
  code,
  header,
  codeTitle,
  shareTitle,
  description,
  variant = "default",
  items = [],
  infoTitle,
  referralPageVariant,
  backgroundImage,
  backgroundImageTablet,
  backgroundImageMobile,
  smallText,
  showCta = false,
  ctaRow,
  className,
  withBottomCta = false,
  bottomCtaLabel = "",
  bottomCtaLink = "",
  isHidden = false,
  isLegacyStyled = false,
  shareCodeVariant,
  shareConfig,
}: ReferralPersonalCodeVariantButtonPropsType) => {
  const isWithBackgroundImageVariant = variant === "withBackgroundImage";

  const classes = classNames(styles.personalCode, className, {
    [styles.dashboardVariant]: variant === "dashboard",
    [styles.withBackgroundImage]: isWithBackgroundImageVariant,
    [styles.referralPageVariant]: referralPageVariant,
    [styles.shareCodeVariantShareButton]: true,
    [styles.shareCodeVariantShareButtonStyle]: true,
    [styles.showCta]: showCta,
    [styles.legacyStyle]: isLegacyStyled,
  });

  const [isShareDialogOpen, setShareDialogOpen] = useState(false);

  const handleBottomCtaClick = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: bottomCtaLabel,
      cta_location: "referral-personal-code",
    });
  };

  const shareUrl = shareConfig?.shareUrl;
  const resolvedShareUrl = useMemo(() => {
    if (!(shareUrl && code)) return null;
    return replaceUrlCode(shareUrl, code);
  }, [shareUrl, code]);

  const handleShareClick = useCallback(
    async ({ skipNative }: { skipNative?: boolean } = {}) => {
      if (!shareConfig) return;
      dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
        cta_name: shareConfig.buttonLabel ?? "",
        cta_location: "referral-personal-code",
      });

      const text = shareConfig.shareTextPrefix ? shareConfig.shareTextPrefix : undefined;

      if (skipNative) {
        setShareDialogOpen(true);
        return;
      }

      const nativeResult = await tryNativeShare({ text, url: resolvedShareUrl ?? undefined });
      // Only show fallback dialog if native share is not supported
      // Don't show it if user cancelled the native share dialog
      if (nativeResult === "unsupported") {
        setShareDialogOpen(true);
      }
    },
    [shareConfig, resolvedShareUrl]
  );

  if (isHidden) {
    return null;
  }

  if (isWithBackgroundImageVariant) {
    return (
      <div className={styles.shareCodeVariantShareButton} data-testid="referral-personal-code-wrapper">
        <ReferralPersonalCodeWithBackground
          items={[]}
          backgroundImage={backgroundImage}
          backgroundImageTablet={backgroundImageTablet}
          backgroundImageMobile={backgroundImageMobile}
          title={codeTitle}
          subtitle={header}
          description={description}
          code={code}
          infoTitle={infoTitle}
          withBottomCta={withBottomCta}
          bottomCtaLabel={bottomCtaLabel}
          bottomCtaLink={bottomCtaLink}
          handleBottomCtaClick={handleBottomCtaClick}
          ctaRow={ctaRow}
          shareCodeVariant
          shareButtonLabel={shareConfig?.buttonLabel}
          onShareClick={handleShareClick}
          shareTitle={shareTitle}
        />
        {shareConfig && (
          <ReferralPersonalCodeShareDialog
            open={isShareDialogOpen}
            onClose={() => setShareDialogOpen(false)}
            providers={shareConfig.providers}
            shareUrl={resolvedShareUrl ?? ""}
            shareTextPrefix={shareConfig.shareTextPrefix}
            title={shareConfig.shareDialogTitle}
            description={shareConfig.shareDialogDescription}
            messenger={shareConfig.messenger}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {topHeader && (
        <div className={styles.topHeader} data-testid="referral-personal-code__topHeader">
          {topHeader}
        </div>
      )}
      <div className={classes} data-testid="referral-personal-code">
        <div className={styles.title} data-testid={"your-personal-code-header"}>
          {codeTitle}
        </div>
        {description && (
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: sanitize(description),
            }}
          />
        )}
        {showCta && ctaRow ? (
          ctaRow
        ) : (
          <ReferralPersonalCodeBlocks
            code={code}
            shareButtonLabel={shareConfig?.buttonLabel}
            onShareClick={handleShareClick}
            items={items}
            infoTitle={infoTitle}
            shareTitle={shareTitle ?? ""}
            shareCodeVariant={shareCodeVariant}
          />
        )}
        <ReferralPersonalCodeSmallText
          smallText={smallText}
          isWithBackgroundImageVariant={isWithBackgroundImageVariant}
        />
        {withBottomCta && (
          <div className={styles.bottomCta}>
            <Button
              variant="text"
              type="cta"
              href={bottomCtaLink}
              onClick={handleBottomCtaClick}
              data-testid="bottomCta"
              aria-label={bottomCtaLabel}
            >
              {bottomCtaLabel}
            </Button>
          </div>
        )}
      </div>
      {resolvedShareUrl && shareConfig && (
        <ReferralPersonalCodeShareDialog
          open={isShareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          providers={shareConfig.providers}
          shareUrl={resolvedShareUrl}
          shareTextPrefix={shareConfig.shareTextPrefix}
          title={shareConfig.shareDialogTitle}
          description={shareConfig.shareDialogDescription}
          messenger={shareConfig.messenger}
        />
      )}
    </>
  );
};
